import React, { useCallback, useEffect, useRef, useState } from "react";
import api, { API, downloadFile, importFile } from "@/lib/api";
import { rupiah, num, packDisplay } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Pencil, ImagePlus, Trash2, Download, Upload, FileSpreadsheet } from "lucide-react";
import { toast } from "sonner";

const UNITS = ["PCS", "BOX", "DUS", "KG", "LITER", "PACK"];
const PACK_UNITS = ["BOX", "DUS", "LUSIN", "PACK", "KG", "LITER"];

const EMPTY_FORM = { sku: "", name: "", category: "", unit: "PCS", cost_price: 0, sell_price: 0, min_stock: 0, photo_path: "", packs: [], kind: "finished", price_tiers: [] };

function tiersToArray(tiers) {
  return Object.entries(tiers || {}).map(([code, v]) =>
    typeof v === "object" && v !== null ? { code, price: v.price, unit: v.unit || "" } : { code, price: v, unit: "" }
  );
}
function tiersToDict(arr) {
  const out = {};
  (arr || []).forEach((t) => {
    if (t.code && Number(t.price) > 0) out[t.code.toUpperCase()] = t.unit ? { price: Number(t.price), unit: t.unit } : Number(t.price);
  });
  return out;
}

function stockCls(r) {
  if (r.stock_warehouse <= 0) return "text-rose-600";
  if (r.stock_warehouse <= r.min_stock) return "text-amber-700";
  return "";
}

export default function Products() {
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [uploading, setUploading] = useState(false);

  const load = useCallback(async () => {
    const r = await api.get("/products");
    setRows(r.data);
  }, []);
  useEffect(() => { load(); }, [load]);

  const filtered = rows.filter((r) => !q || r.sku.toLowerCase().includes(q.toLowerCase()) || r.name.toLowerCase().includes(q.toLowerCase()));

  const openNew = () => {
    setEditing(null);
    setForm(EMPTY_FORM);
    setOpen(true);
  };
  const openEdit = (r) => {
    setEditing(r);
    setForm({ ...r, packs: r.packs || [], price_tiers: tiersToArray(r.price_tiers), kind: r.kind || "finished" });
    setOpen(true);
  };

  const addPack = () => setForm((s) => ({ ...s, packs: [...(s.packs || []), { unit: "DUS", factor: 12 }] }));
  const setPack = (i, k, v) => setForm((s) => {
    const packs = [...(s.packs || [])];
    packs[i] = { ...packs[i], [k]: k === "factor" ? Number(v) : v };
    return { ...s, packs };
  });
  const removePack = (i) => setForm((s) => ({ ...s, packs: (s.packs || []).filter((_, idx) => idx !== i) }));

  const fileRef = useRef(null);

  const remove = async (r) => {
    if (!window.confirm(`Hapus barang ${r.name}? Barang akan dinonaktifkan.`)) return;
    try { await api.delete(`/products/${r.product_id}`); toast.success("Barang dihapus"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Gagal menghapus"); }
  };

  const doImport = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    try {
      const r = await importFile("/products/import", f);
      toast.success(`Import selesai: ${r.data.created} ditambah, ${r.data.skipped} dilewati (SKU sudah ada)`);
      if (r.data.errors?.length) toast.warning(r.data.errors.join("; "));
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Gagal import"); }
  };

  const save = async () => {
    try {
      const payload = { ...form, price_tiers: tiersToDict(form.price_tiers) };
      if (editing) await api.put(`/products/${editing.product_id}`, payload);
      else await api.post("/products", payload);
      toast.success("Barang tersimpan");
      setOpen(false); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Gagal simpan"); }
  };

  const upload = async (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    const fd = new FormData(); fd.append("file", f);
    setUploading(true);
    try {
      const r = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setForm((s) => ({ ...s, photo_path: r.data.storage_path }));
      toast.success("Foto diunggah");
    } catch { toast.error("Gagal unggah"); }
    finally { setUploading(false); }
  };

  return (
    <div data-testid="page-products" className="space-y-6">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <div className="text-xs font-mono uppercase tracking-widest text-slate-500 mb-2">Master Data</div>
          <h1 className="font-display text-3xl font-bold text-slate-900">Master Barang</h1>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Input placeholder="Cari SKU / nama..." value={q} onChange={(e) => setQ(e.target.value)} className="w-56" data-testid="search-products" />
          <Button variant="outline" size="sm" onClick={() => downloadFile("/products/template", "template_barang.xlsx")} data-testid="btn-template"><FileSpreadsheet className="h-4 w-4 mr-1" />Template</Button>
          <Button variant="outline" size="sm" onClick={() => downloadFile("/products/export", "daftar_barang.xlsx")} data-testid="btn-export"><Download className="h-4 w-4 mr-1" />Export</Button>
          <Button variant="outline" size="sm" onClick={() => fileRef.current?.click()} data-testid="btn-import"><Upload className="h-4 w-4 mr-1" />Import</Button>
          <input ref={fileRef} type="file" accept=".xlsx" className="hidden" onChange={doImport} data-testid="input-import" />
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button onClick={openNew} className="bg-slate-900 hover:bg-slate-800" data-testid="btn-new-product">
                <Plus className="h-4 w-4 mr-1" /> Tambah Barang
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-lg">
              <DialogHeader><DialogTitle>{editing ? "Ubah Barang" : "Tambah Barang"}</DialogTitle></DialogHeader>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>SKU</Label><Input value={form.sku} onChange={(e) => setForm({ ...form, sku: e.target.value.toUpperCase() })} data-testid="input-sku" /></div>
                <div><Label>Kategori</Label><Input value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} data-testid="input-category" /></div>
                <div className="col-span-2"><Label>Nama</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="input-name" /></div>
                <div><Label>Satuan</Label>
                  <Select value={form.unit} onValueChange={(v) => setForm({ ...form, unit: v })}>
                    <SelectTrigger data-testid="input-unit"><SelectValue /></SelectTrigger>
                    <SelectContent>{UNITS.map(u => <SelectItem key={u} value={u}>{u}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div><Label>Jenis</Label>
                  <Select value={form.kind} onValueChange={(v) => setForm({ ...form, kind: v })}>
                    <SelectTrigger data-testid="input-kind"><SelectValue /></SelectTrigger>
                    <SelectContent><SelectItem value="finished">Barang Jadi</SelectItem><SelectItem value="raw">Bahan Baku</SelectItem></SelectContent>
                  </Select>
                </div>
                <div><Label>Stok Minimum</Label><Input type="number" value={form.min_stock} onChange={(e) => setForm({ ...form, min_stock: e.target.value })} data-testid="input-min-stock" /></div>
                <div><Label>Harga Modal (Rp)</Label><Input type="number" value={form.cost_price} onChange={(e) => setForm({ ...form, cost_price: e.target.value })} data-testid="input-cost" /></div>
                <div><Label>Harga Jual (Rp)</Label><Input type="number" value={form.sell_price} onChange={(e) => setForm({ ...form, sell_price: e.target.value })} data-testid="input-sell" /></div>
                <div className="col-span-2 border border-dashed border-slate-300 rounded p-3 space-y-2">
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="text-sm font-medium">Harga Jual Bertingkat (Kode Harga)</div>
                      <div className="text-xs text-slate-500">Contoh: kode A = Rp 40.000, GROSIR = Rp 38.000. Pelanggan dengan kode itu otomatis mendapat harga ini di invoice.</div>
                    </div>
                    <Button type="button" variant="outline" size="sm" onClick={() => setForm((s) => ({ ...s, price_tiers: [...(s.price_tiers || []), { code: "", price: 0 }] }))} data-testid="btn-add-tier"><Plus className="h-3 w-3 mr-1" />Kode</Button>
                  </div>
                  {(form.price_tiers || []).map((t, i) => (
                    <div key={`${t.code}-${i}`} className="grid grid-cols-[100px_1fr_110px_36px] gap-2 items-center">
                      <Input placeholder="Kode (A/B)" value={t.code} onChange={(e) => setForm((s) => { const pt = [...s.price_tiers]; pt[i] = { ...pt[i], code: e.target.value.toUpperCase() }; return { ...s, price_tiers: pt }; })} data-testid={`tier-${i}-code`} />
                      <Input type="number" placeholder="Harga Rp" value={t.price} onChange={(e) => setForm((s) => { const pt = [...s.price_tiers]; pt[i] = { ...pt[i], price: e.target.value }; return { ...s, price_tiers: pt }; })} data-testid={`tier-${i}-price`} />
                      <Select value={t.unit || form.unit} onValueChange={(v) => setForm((s) => { const pt = [...s.price_tiers]; pt[i] = { ...pt[i], unit: v === form.unit ? "" : v }; return { ...s, price_tiers: pt }; })}>
                        <SelectTrigger data-testid={`tier-${i}-unit`}><SelectValue /></SelectTrigger>
                        <SelectContent>{[form.unit, ...(form.packs || []).map(pk => pk.unit)].map(u => <SelectItem key={u} value={u}>per {u}</SelectItem>)}</SelectContent>
                      </Select>
                      <Button type="button" variant="ghost" size="icon" onClick={() => setForm((s) => ({ ...s, price_tiers: s.price_tiers.filter((_, idx) => idx !== i) }))}>×</Button>
                    </div>
                  ))}
                </div>
                <div className="col-span-2 border border-dashed border-slate-300 rounded p-3 space-y-2">
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="text-sm font-medium">Konversi Satuan Kemasan</div>
                      <div className="text-xs text-slate-500">Contoh: 1 DUS = 12 {form.unit}. Transaksi boleh input dalam satuan kemasan — stok otomatis dikonversi ke {form.unit}.</div>
                    </div>
                    <Button type="button" variant="outline" size="sm" onClick={addPack} data-testid="btn-add-pack"><Plus className="h-3 w-3 mr-1" />Satuan</Button>
                  </div>
                  {(form.packs || []).map((pk, i) => (
                    <div key={pk.unit} className="grid grid-cols-[110px_1fr_40px] gap-2 items-center">
                      <Select value={pk.unit} onValueChange={(v) => setPack(i, "unit", v)}>
                        <SelectTrigger data-testid={`pack-${i}-unit`}><SelectValue /></SelectTrigger>
                        <SelectContent>{PACK_UNITS.filter(u => u !== form.unit).map(u => <SelectItem key={u} value={u}>{u}</SelectItem>)}</SelectContent>
                      </Select>
                      <div className="flex items-center gap-2">
                        <span className="text-xs text-slate-500 whitespace-nowrap">1 {pk.unit} =</span>
                        <Input type="number" min={2} value={pk.factor} onChange={(e) => setPack(i, "factor", e.target.value)} data-testid={`pack-${i}-factor`} />
                        <span className="text-xs text-slate-500">{form.unit}</span>
                      </div>
                      <Button type="button" variant="ghost" size="icon" onClick={() => removePack(i)}>×</Button>
                    </div>
                  ))}
                </div>
                <div className="col-span-2">
                  <Label>Foto Barang</Label>
                  <div className="flex items-center gap-3">
                    <label className="cursor-pointer inline-flex items-center gap-2 border border-slate-200 rounded px-3 py-2 hover:bg-slate-50 text-sm">
                      <ImagePlus className="h-4 w-4" /> {uploading ? "Mengunggah..." : "Pilih foto"}
                      <input type="file" accept="image/*" className="hidden" onChange={upload} data-testid="input-photo" />
                    </label>
                    {form.photo_path && <span className="text-xs text-slate-500 font-mono truncate">{form.photo_path.split("/").pop()}</span>}
                  </div>
                </div>
              </div>
              <DialogFooter><Button onClick={save} className="bg-slate-900 hover:bg-slate-800" data-testid="btn-save-product">Simpan</Button></DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      <Card>
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="table-products">
            <thead>
              <tr className="border-b bg-slate-50 text-left text-xs font-mono uppercase tracking-wider text-slate-500">
                <th className="py-2 px-4">Foto</th>
                <th className="py-2 px-4">SKU</th>
                <th className="py-2 px-4">Nama</th>
                <th className="py-2 px-4">Kategori</th>
                <th className="py-2 px-4 text-right">Harga Modal</th>
                <th className="py-2 px-4 text-right">Harga Jual</th>
                <th className="py-2 px-4 text-right">Stok Gudang</th>
                <th className="py-2 px-4"></th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((r) => (
                <tr key={r.product_id} className="border-b hover:bg-slate-50/50">
                  <td className="py-2 px-4">
                    {r.photo_path ? (
                      <img src={`${API}/files/${r.photo_path}`} alt="" className="h-10 w-10 rounded object-cover" />
                    ) : <div className="h-10 w-10 rounded bg-slate-100" />}
                  </td>
                  <td className="py-2 px-4 font-mono text-xs">{r.sku}</td>
                  <td className="py-2 px-4"><div className="font-medium">{r.name}</div><div className="text-xs text-slate-500">{r.unit}{(r.packs || []).map(pk => ` · 1 ${pk.unit}=${pk.factor}`).join("")}</div></td>
                  <td className="py-2 px-4 text-slate-600">{r.category}{r.kind === "raw" && <span className="ml-1 text-[10px] font-mono bg-amber-100 text-amber-800 rounded px-1" data-testid={`kind-${r.sku}`}>Bahan Baku</span>}</td>
                  <td className="py-2 px-4 text-right font-mono">{rupiah(r.cost_price)}</td>
                  <td className="py-2 px-4 text-right font-mono">{rupiah(r.sell_price)}
                    {Object.keys(r.price_tiers || {}).length > 0 && <div className="text-[10px] text-slate-500">{Object.entries(r.price_tiers).map(([c, pr]) => `${c}: ${rupiah(typeof pr === "object" ? pr.price : pr)}${typeof pr === "object" && pr.unit ? "/" + pr.unit : ""}`).join(" · ")}</div>}
                  </td>
                  <td className="py-2 px-4 text-right font-mono">
                    <span className={stockCls(r)}>{num(r.stock_warehouse)} {r.unit}</span>
                    {packDisplay(r.stock_warehouse, r.unit, r.packs) && <div className="text-[10px] text-slate-500">{packDisplay(r.stock_warehouse, r.unit, r.packs)}</div>}
                  </td>
                  <td className="py-2 px-4 whitespace-nowrap">
                    <Button variant="ghost" size="sm" onClick={() => openEdit(r)} data-testid={`edit-${r.sku}`}><Pencil className="h-3.5 w-3.5" /></Button>
                    <Button variant="ghost" size="sm" onClick={() => remove(r)} data-testid={`delete-${r.sku}`}><Trash2 className="h-3.5 w-3.5 text-rose-600" /></Button>
                  </td>
                </tr>
              ))}
              {filtered.length === 0 && <tr><td colSpan={8} className="py-10 text-center text-slate-400">Belum ada barang. Klik "Isi Demo Data" di atas atau "Tambah Barang".</td></tr>}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}
